import * as React from 'react';
import { ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';

interface CollapsibleSectionProps {
  title: string;
  icon?: ReactNode;
  defaultOpen?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  summary?: ReactNode;
  children: ReactNode;
  className?: string;
  testId?: string;
}

export function CollapsibleSection({
  title,
  icon,
  defaultOpen = true,
  open,
  onOpenChange,
  summary,
  children,
  className,
  testId,
}: CollapsibleSectionProps) {
  const [internalOpen, setInternalOpen] = React.useState(defaultOpen);
  const isOpen = open !== undefined ? open : internalOpen;

  const handleOpenChange = (newOpen: boolean) => {
    setInternalOpen(newOpen);
    onOpenChange?.(newOpen);
  };

  return (
    <Collapsible
      open={open}
      defaultOpen={defaultOpen}
      onOpenChange={handleOpenChange}
      className={cn('rounded-lg', className)}
      data-testid={testId}
    >
      <CollapsibleTrigger
        className="w-full flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg transition-colors"
        data-testid={testId ? `${testId}-trigger` : undefined}
      >
        <div className="flex items-center gap-2">
          {icon}
          <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
        </div>
        <div className="flex items-center gap-3">
          {!isOpen && summary && (
            <div className="text-xs text-gray-500 truncate max-w-[300px]" data-testid={testId ? `${testId}-summary` : undefined}>
              {summary}
            </div>
          )}
          <ChevronDown
            className={cn(
              'w-4 h-4 text-gray-500 transition-transform duration-200',
              !isOpen && '-rotate-90'
            )}
          />
        </div>
      </CollapsibleTrigger>

      <CollapsibleContent>
        <div className="px-3 pb-3">
          {children}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
